// AccountSettings.jsx — Réglages du compte (web) : infos perso + mot de passe.
import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Loader2, Save, KeyRound } from 'lucide-react';

export default function AccountSettings() {
  const { user, refreshUser } = useAuth();
  const [prenom, setPrenom] = useState(user?.prenom || '');
  const [nom, setNom] = useState(user?.nom || '');
  const [email, setEmail] = useState(user?.email || '');
  const [savingInfo, setSavingInfo] = useState(false);

  const [ancien, setAncien] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirme, setConfirme] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const saveInfo = async () => {
    if (!prenom.trim() || !nom.trim() || !email.trim()) return toast.error('Tous les champs sont requis');
    setSavingInfo(true);
    try {
      const { data } = await api.put('/account/profile', { prenom, nom, email });
      await refreshUser();
      if (data?.email_verification_sent) {
        toast.success('Un email de confirmation a été envoyé à votre nouvelle adresse');
      } else {
        toast.success('Vos informations ont été mises à jour');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingInfo(false);
    }
  };

  const savePwd = async () => {
    if (!ancien || !nouveau) return toast.error('Renseignez les deux mots de passe');
    if (nouveau.length < 6) return toast.error('Le nouveau mot de passe doit faire au moins 6 caractères');
    if (nouveau !== confirme) return toast.error('La confirmation ne correspond pas');
    setSavingPwd(true);
    try {
      await api.post('/account/change-password', { ancien_mot_de_passe: ancien, nouveau_mot_de_passe: nouveau });
      toast.success('Mot de passe modifié');
      setAncien(''); setNouveau(''); setConfirme('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <>
      <div className="card mb-6" data-testid="account-info-card">
        <h2 className="font-display text-lg font-bold text-[#0F172A] mb-4">Mes informations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-uppercase block mb-1.5">Prénom</label>
            <input className="ap-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="account-info-prenom" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Nom</label>
            <input className="ap-input" value={nom} onChange={(e) => setNom(e.target.value)} data-testid="account-info-nom" />
          </div>
        </div>
        <div className="mb-4">
          <label className="label-uppercase block mb-1.5">Email</label>
          <input type="email" className="ap-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-info-email" />
        </div>
        <button onClick={saveInfo} disabled={savingInfo} className="btn-primary inline-flex items-center gap-2" data-testid="account-info-save">
          {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>

      <div className="card mb-6" data-testid="account-pwd-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-[#475569]" />
          <h2 className="font-display text-lg font-bold text-[#0F172A]">Changer mon mot de passe</h2>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <label className="label-uppercase block mb-1.5">Mot de passe actuel</label>
            <input type="password" className="ap-input" value={ancien} onChange={(e) => setAncien(e.target.value)} placeholder="••••••••" data-testid="account-pwd-old" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Nouveau mot de passe</label>
              <input type="password" className="ap-input" value={nouveau} onChange={(e) => setNouveau(e.target.value)} placeholder="6 caractères min." data-testid="account-pwd-new" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Confirmer</label>
              <input type="password" className="ap-input" value={confirme} onChange={(e) => setConfirme(e.target.value)} placeholder="••••••••" data-testid="account-pwd-confirm" />
            </div>
          </div>
        </div>
        <button onClick={savePwd} disabled={savingPwd} className="btn-secondary inline-flex items-center gap-2" data-testid="account-pwd-save">
          {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Mettre à jour le mot de passe
        </button>
      </div>
    </>
  );
}
